import type { DealStatus } from "@/api/client";

/**
 * Shared deal-expiry predicates for the PO Insights surface.
 *
 * Both the "Expiring Deals" metric card (routes/.../insights/index.tsx) and the
 * Expiring drill-down drawer (DealMetricDrawer.tsx) must agree on which live
 * deals are "expiring within N days" — otherwise the card count and the
 * drawer's Deal Count / ledger diverge (e.g. a card using a raw-ms `close <=
 * now + 30d` compare vs. a drawer flooring the day delta). This module is the
 * single source of truth so they can never drift apart.
 *
 * The predicates only read `deal_status` + `target_close_date`, so they accept
 * any deal shape carrying those (ManagerDealListItem, DealListItem, …) — CHP-798
 * reuses them for the partner "Deals expiring" scorecard off the BDM/PO list.
 */
type ExpirableDeal = {
  deal_status: DealStatus;
  target_close_date: string | null;
};

/** Days until a live deal's close date; null when not live or no close date.
 *  Past-dated live deals return a negative number (overdue = same risk). */
export function daysToClose(deal: ExpirableDeal, now: number): number | null {
  if (deal.deal_status !== "live" || !deal.target_close_date) return null;
  const close = new Date(deal.target_close_date).getTime();
  if (Number.isNaN(close)) return null;
  return Math.floor((close - now) / (24 * 60 * 60 * 1000));
}

/** True when a live deal closes within `days` of `now` (inclusive of overdue). */
export function isExpiringWithin(deal: ExpirableDeal, now: number, days: number): boolean {
  const d = daysToClose(deal, now);
  return d !== null && d <= days;
}
